// @flow

import fp from "lodash/fp"
import type { OrderedSet } from "immutable"
import * as R from "ramda"
import Passenger from "@kiwicom/frontend-app/scripts/containers/mutable/Passenger"

import AdditionalService from "common/records/AdditionalService"
import { SEATING_CHANGES_STATUSES } from "mmb/consts/booking"
import type { FlightId, PassengerId } from "mmb/types/primitiveTypes"
import type {
  RequestedSeats,
  RequestedSeatsArray,
  Seatmap,
  Seatmaps,
} from "./components/BookingSeatmap/types"
import type { PassengersSeat } from "./components/BookingSeatingFlight/types"
import type { AdditionalServicesStateInst } from "../../services/additionalServices/types"
import type { SeatingAncillary } from "../../scenes/BookingAncillary/consts/types"

export const SEATING_API_STATUS = {
  UNEXPECTED_ERROR: "unexpected_error",
}

export const SEATING_STATUS = {
  API_ERROR: "API_ERROR",
  AVAILABLE_VIA_AUTOMATS: "AVAILABLE_VIA_AUTOMATS",
  AVAILABLE_VIA_SUPPORT: "AVAILABLE_VIA_SUPPORT",
  ERROR: "ERROR",
  LOADING: "LOADING",
  SUCCESS_VIA_AUTOMAT: "SUCCESS_VIA_AUTOMAT",
  SUCCESS_VIA_SUPPORT: "SUCCESS_VIA_SUPPORT",
  UNAVAILABLE: "UNAVAILABLE",
  UNEXPECTED_ERROR: "UNEXPECTED_ERROR",
  ALL_ASSIGNED: "ALL_ASSIGNED",
}

export const getSeatId = (rowId: number, column: string) => `${rowId}-${column}`

export const flattenSeatmap = (input: Seatmap) => {
  if (!input) {
    return {}
  }

  return fp.memoize(
    fp.compose(
      fp.reduce(
        (seatmap, [rowId, groupedSeats]) =>
          fp.compose(
            fp.merge(seatmap),
            fp.keyBy(seat => getSeatId(rowId, seat.column)),
            fp.filter(fp.propEq("type", "seat")),
            fp.flatten,
          )(groupedSeats),
        {},
      ),
      fp.entries,
    ),
  )(input)
}

export const bookedSeatsPriceFactory = (priceType: "price" | "margin") => (
  seatmaps: Seatmaps, // fid: Seatmap
  bookedSeats: { [flightId: FlightId]: { [PassengerId: PassengerId]: string } }, // fid, pid, seatId
) => {
  const basePrice = 0

  if (!seatmaps) {
    return basePrice
  }

  return fp.compose(
    fp.reduce(fp.add, basePrice),
    fp.map(([seatmapId, seatmapRecord]) =>
      fp.compose(
        fp.sumBy(([, seat]) => fp.prop(`${priceType}.amount`, seat)),
        fp.filter(([seatId]) => fp.includes(seatId, bookedSeats[seatmapId])),
        fp.entries,
        fp.curry(flattenSeatmap),
      )(seatmapRecord.seatmap),
    ),
    fp.entries,
  )(seatmaps)
}

export const bookedSeatsBasePrice = bookedSeatsPriceFactory("price")
export const bookedSeatsMarginPrice = bookedSeatsPriceFactory("margin")

export const mapAncillaryDataToSeating = (
  ancillariesSeatingOrders: SeatingAncillary,
): RequestedSeatsArray => {
  /**
   * pick all seats from ancillaries so it can map them later to requestedSeats shape
   * and also add segment code to every ordered seat
   */
  const getAllSeatsOnly = R.reduce((acc, val) => {
    return [
      ...acc,
      ...val.items.map(item => [
        ...item.seats.map(seat => ({
          ...seat,
          segmentCode: item.segmentCode,
          option: item.option,
        })),
      ]),
    ]
  }, [])

  /**
   * use flattened array from getAllSeatsOnly() and map it to requestedSeats
   * val.bookedSeat || val.seat => if pap chose seat on MMB we should display that
   * even if it's not confirmed
   */
  const mapSeatsArrayToAdditionalServicesShape = R.reduce((acc, val) => {
    if (acc[val.segmentCode]) {
      return {
        ...acc,
        [val.segmentCode]: {
          // $FlowFixMe
          ...acc[[val.segmentCode]],
          [val.passengerId]: { seat: val.bookedSeat || val.seat, option: val.option },
        },
      }
    }
    return {
      ...acc,
      [val.segmentCode]: {
        [val.passengerId]: { seat: val.bookedSeat || val.seat, option: val.option },
      },
    }
  }, {})

  const requestedSeats = R.compose(
    mapSeatsArrayToAdditionalServicesShape,
    R.flatten,
    getAllSeatsOnly,
  )(ancillariesSeatingOrders)

  if (!Object.keys(requestedSeats).length) {
    return []
  }

  return [
    {
      requestedSeats,
      status: "confirmed",
    },
  ]
}

/*
 * Processes seatmap changes so it can be simply used to check against seats during
 * seatmap render.
 */
export const processSeatmapAdditionalServices = (
  requestedSeats: RequestedSeatsArray,
  flightId: FlightId,
): RequestedSeats =>
  fp.compose(
    fp.reduce(fp.merge, {}),
    fp.flatMap(additionalService =>
      fp.compose(
        fp.map(([passenger, seat]) => ({
          [passenger]: {
            seat: seat.seat,
            status: additionalService.status,
            option: seat.option,
          },
        })),
        fp.entries,
        fp.prop(["requestedSeats", flightId]),
      )(additionalService),
    ),
    fp.reject(
      // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
      fp.propEq("status", SEATING_CHANGES_STATUSES.REFUNDED) ||
        fp.propEq("status", SEATING_CHANGES_STATUSES.CANCELLED),
    ),
  )(requestedSeats)

export const seatmapAvailableSize = (seatmaps: Seatmaps): number =>
  fp.memoize(
    fp.compose(
      fp.size,
      fp.filter(fp.propEq("status", "available")),
    ),
  )(seatmaps) // eslint-disable-line flowtype/no-weak-types

export const seatmapUnavailableSize = (seatmaps: Seatmaps): number =>
  fp.memoize(
    fp.compose(
      fp.size,
      fp.filter(fp.propEq("status", "unavailable")),
    ),
  )(seatmaps) // eslint-disable-line flowtype/no-weak-types

export const seatmapManuallySize = (seatmaps: Seatmaps): number =>
  fp.memoize(
    fp.compose(
      fp.size,
      fp.filter(fp.propEq("status", "manually")),
    ),
  )(seatmaps) // eslint-disable-line flowtype/no-weak-types

export const availableViaAutomats = (
  seatmaps: Seatmaps,
): boolean => // eslint-disable-line flowtype/no-weak-types
  // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
  fp.compose(
    sizes => sizes[0] === 0 && sizes[1] > 0,
    fp.over([seatmapManuallySize, seatmapAvailableSize]),
  )(seatmaps)

export const availableViaSupport = (
  seatmaps: Seatmaps,
): boolean => // eslint-disable-line flowtype/no-weak-types
  // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
  fp.compose(
    sizes => sizes[0] === 0 && sizes[1] > 0,
    fp.over([seatmapUnavailableSize, seatmapManuallySize]),
  )(seatmaps)

export const seatingApiError = (status: $Values<typeof SEATING_API_STATUS>) =>
  status === SEATING_API_STATUS.UNEXPECTED_ERROR

// When to show seating modal in BookingSeatmap.js
export const showSeating = (status: $Values<typeof SEATING_STATUS>) =>
  status === SEATING_STATUS.API_ERROR ||
  status === SEATING_STATUS.AVAILABLE_VIA_AUTOMATS ||
  status === SEATING_STATUS.LOADING

export const showUnavailable = (status: $Values<typeof SEATING_STATUS>) =>
  status === SEATING_STATUS.UNAVAILABLE

// When to show seatmap in BookingSeatmap.js
export const showSeatmap = (status: $Values<typeof SEATING_STATUS>) =>
  status === SEATING_STATUS.AVAILABLE_VIA_AUTOMATS

// When to show Booking.js header
export const showHeader = (status: $Values<typeof SEATING_STATUS>) =>
  status === SEATING_STATUS.SUCCESS_VIA_AUTOMAT ||
  status === SEATING_STATUS.SUCCESS_VIA_SUPPORT ||
  status === SEATING_STATUS.ERROR ||
  status === SEATING_STATUS.AVAILABLE_VIA_SUPPORT

export const getSeatingModalStatus = (
  seatmaps: Seatmaps,
  additionalServices: AdditionalServicesStateInst,
  loading: boolean,
  passengers: Array<Passenger>,
  seatingAdditionalServices: OrderedSet<AdditionalService>,
  ancillariesLoading: boolean,
  ancillariesIsError: boolean,
) => {
  // $FlowFixMe
  const seatmapsStatus = seatmaps && seatmaps.status // not able to find status like this in API

  if (seatmaps) {
    const allFlightsUnavailable = Object.keys(seatmaps)
      .map(
        flight =>
          Object.keys(processSeatmapAdditionalServices(seatingAdditionalServices, flight))
            .length === passengers.length ||
          ["unavailable", "service_window_closed", "fare_category_unsupported"].includes(
            seatmaps[flight].status,
          ),
      )
      .every(f => f)

    if (allFlightsUnavailable) {
      return SEATING_STATUS.UNAVAILABLE
    }
  }

  if (additionalServices.success) {
    return availableViaAutomats(seatmaps)
      ? SEATING_STATUS.SUCCESS_VIA_AUTOMAT
      : SEATING_STATUS.SUCCESS_VIA_SUPPORT
  }
  // $FlowFixMe
  if (additionalServices.error || seatingApiError(seatmapsStatus) || ancillariesIsError) {
    return SEATING_STATUS.ERROR
  }
  if (loading || ancillariesLoading) {
    return SEATING_STATUS.LOADING
  }
  if (seatmaps && Object.keys(seatmaps).length === 0) {
    return SEATING_STATUS.UNAVAILABLE
  }
  if (!seatmaps || Object.keys(seatmaps).length === 0) {
    return SEATING_STATUS.API_ERROR
  }
  if (availableViaAutomats(seatmaps)) {
    return SEATING_STATUS.AVAILABLE_VIA_AUTOMATS
  }
  if (availableViaSupport(seatmaps)) {
    return SEATING_STATUS.AVAILABLE_VIA_SUPPORT
  }
  return SEATING_STATUS.UNAVAILABLE
}

export const getPassengersSeat = (
  passengersWithSeat: Array<Passenger>,
  requestedSeats: RequestedSeats,
): PassengersSeat =>
  passengersWithSeat.map(p => ({
    name: p.getFullNameWithoutTitle(),
    seat: requestedSeats[p.id]?.seat,
    birth: p.birth,
    option: requestedSeats[p.id]?.option,
  }))
